import { IEvent } from '../../store/events/interfaces';
import { IProgram } from '../../store/program/interfaces';
import { IRegistration } from '../../store/registrations/interfaces';
import { ToastType } from '../../store/misc/interfaces';

interface IToast {
  body: string;
  type: ToastType;
  title: string;
  duration?: number;
}

const createError = (eventName: string, bodyMsg: string, type: ToastType, title: string, duration?: number): IToast => ({
  body: `${bodyMsg} "${eventName}" entdeckt.`,
  type,
  title,
  duration,
});

const createNotification = (eventName: string, bodyMsg: string, type: ToastType, title: string, duration?: number): IToast => ({
  body: `${bodyMsg}`,
  type,
  title,
  duration,
});

const checkTimeConflict = (eventName: string, program: IProgram): IToast | null => {
  if((program.distribution_type === 'INSTANT' && program.resolve_time_conflict_after_payment) || program.distribution_type === 'DEADLINE') {
    return createNotification(eventName, `Die gewählte Veranstaltung überschneidet sich mit '${eventName}'. Bei der Zuteilung werden Sie nur einer Veranstaltung zugeteilt.`, ToastType.Info, 'Zeitkonflikt', 8000);
  } else {
    return createError(eventName, 'Zeitkonflikt mit', ToastType.Danger, 'Fehler');
  }
}

export const checkRegIntegrity = (event: IEvent, regs: IRegistration[], program: IProgram): IToast | null => {
  if (program.max_registrations_per_participant && program.max_registrations_per_participant <= regs.length) {
    return {
      body: `Maximale Anzahl von Veranstaltungen (${program.max_registrations_per_participant}) wurden bereits ausgewählt.`,
      type: ToastType.Danger,
      title: 'Fehler',
    };
  }

  if (event.registration_statistics.event_occupation_bar_data.total_per >= 1 && event.registration_statistics.is_waiting_list_full) {
    if (program.distribution_type === 'DEADLINE') {
      if (event.registration_statistics.is_max_participants_full) {
        return {
          body: 'Die Veranstaltung ist vollständig besetzt.',
          type: ToastType.Danger,
          title: 'Fehler',
        };
      }
    } else if (program.distribution_type === 'INSTANT') {
      return {
        body: 'Diese Veranstaltung ist vollständig besetzt.',
        type: ToastType.Danger,
        title: 'Fehler',
      };
    }
  }

  for (const reg of regs) {
    const newEventMinAge = event.min_age || 0;
    const newEventMaxAge = event.max_age || 1000;
    const oldEventMinAge = reg.event.min_age || 0;
    const oldEventMaxAge = reg.event.max_age || 1000;

    if (newEventMaxAge < oldEventMinAge || newEventMinAge > oldEventMaxAge) {
      return createError(reg.event.name, 'Alterskonflikt mit', ToastType.Danger, 'Fehler');
    }

    if (reg.event.timespans && event.timespans) {
      for (const oldTimespan of event.timespans) {
        for (const newTimespan of reg.event.timespans) {
          if (newTimespan.start <= oldTimespan.end && oldTimespan.start <= newTimespan.end && !(reg.event.ignore_time_conflicts || event.ignore_time_conflicts)) {
            if (!(newTimespan.hide_hours || oldTimespan.hide_hours)) {
              return checkTimeConflict(reg.event.name, program)
            }
          }
        }
      }
    } else if (reg.event.conflicting_events) {
      for (const conflictingEvent of reg.event.conflicting_events) {
        if (event.event_id.toString() === conflictingEvent.split('/').pop()) {
          return checkTimeConflict(reg.event.name, program);
        }
      }
    }

    if (event.excluded_events && reg.event.excluded_events) {
      const eventName = `${event.name} (#${event.relative_id})`;
      if (reg.event.excluded_events.indexOf(eventName) > -1) {
        if (program.distribution_type === 'INSTANT') {
          return {
            body: `Veranstaltungen "${event.name}" und "${reg.event.name}" dürfen nicht gleichzeitig gewählt werden`,
            type: ToastType.Danger,
            title: 'Sich ausschließende Veranstaltungen',
          };
        } else {
          return {
            body: `Veranstaltungen "${event.name}" und "${reg.event.name}" können nicht gleichzeitig besucht werden. Bei der Zuteilung wird eine davon ausgewählt.`,
            type: ToastType.Info,
            title: 'Sich ausschließende Veranstaltungen',
          };
        }
      }
    }
  }
  return null;
};
